
import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Banknote, MessageCircle } from "lucide-react";
import { CommissionRequest } from "@/types/commission.types";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CommissionRequestCardProps {
  request: CommissionRequest;
}

export function CommissionRequestCard({ request }: CommissionRequestCardProps) {
  console.log("CommissionRequestCard - Request:", request);
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 hover:bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-500";
      case "accepted":
        return "bg-blue-100 hover:bg-blue-200 text-blue-800 dark:bg-blue-900/30 dark:text-blue-500";
      case "in_progress":
        return "bg-indigo-100 hover:bg-indigo-200 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-500";
      case "revision":
        return "bg-purple-100 hover:bg-purple-200 text-purple-800 dark:bg-purple-900/30 dark:text-purple-500";
      case "completed":
        return "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:text-green-500";
      case "rejected":
        return "bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-500";
      default:
        return "bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-900/30 dark:text-gray-500";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Pendente";
      case "accepted":
        return "Aceita";
      case "in_progress":
        return "Em progresso";
      case "revision":
        return "Em revisão";
      case "completed":
        return "Concluída";
      case "rejected":
        return "Rejeitada";
      default:
        return status;
    }
  };

  const createdAtDate = new Date(request.created_at);
  const createdAtFormatted = formatDistanceToNow(createdAtDate, {
    addSuffix: true,
    locale: ptBR
  });

  // Ensure the ID is valid before creating the link
  const linkTo = request.id ? `/commissions/requests/${request.id}` : '#';
  console.log("CommissionRequestCard - Link to:", linkTo, "Request ID:", request.id);

  return (
    <Card className="overflow-hidden flex flex-col h-full">
      <CardContent className="p-4 flex-grow">
        <div className="flex justify-between items-start mb-3">
          <Badge className={`${getStatusColor(request.status)}`}>
            {getStatusLabel(request.status)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {createdAtFormatted}
          </span>
        </div>

        <h3 className="font-bold text-lg mb-2 line-clamp-2">
          {request.description?.substring(0, 50) || "Comissão"}...
        </h3>

        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
          <Clock className="h-4 w-4" />
          <span>
            {request.delivery_deadline
              ? `Entrega: ${new Date(request.delivery_deadline).toLocaleDateString()}`
              : "Sem prazo definido"}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
          <Banknote className="h-4 w-4" />
          <span>
            {request.final_price_usd
              ? `$${request.final_price_usd.toFixed(2)} USD`
              : "Preço não definido"}
            {request.final_price_eth
              ? ` / ${request.final_price_eth.toFixed(4)} ETH`
              : ""}
          </span>
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0">
        {request.id ? (
          <Button variant="default" className="w-full" asChild>
            <Link to={linkTo}>
              <MessageCircle className="h-4 w-4 mr-2" />
              Ver detalhes
            </Link>
          </Button>
        ) : (
          <Button variant="outline" className="w-full" disabled>
            <MessageCircle className="h-4 w-4 mr-2" />
            ID inválido
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
